import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { adminAPI } from "../services/api";
import {
  UserPlus,
  Trash2,
  RefreshCw,
  Mail,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
} from "lucide-react";

const formatDate = (iso) =>
  iso
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(iso))
    : "—";

const StatusBadge = ({ invite }) => {
  const { t } = useTranslation();
  if (invite.usedAt)
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-1 text-xs font-semibold text-emerald-300">
        <CheckCircle className="h-3 w-3" />
        {t("invites.statusUsed")}
      </span>
    );
  if (!invite.isActive)
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-red-500/30 bg-red-500/10 px-2.5 py-1 text-xs font-semibold text-red-300">
        <XCircle className="h-3 w-3" />
        {t("invites.statusRevoked")}
      </span>
    );
  if (invite.expiresAt && new Date(invite.expiresAt) < new Date())
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-xs font-semibold text-amber-300">
        <AlertTriangle className="h-3 w-3" />
        {t("invites.statusExpired")}
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2.5 py-1 text-xs font-semibold text-cyan-300">
      <Clock className="h-3 w-3" />
      {t("invites.statusPending")}
    </span>
  );
};

const CreatedCodeModal = ({ code, onClose }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg rounded-3xl border border-amber-500/30 bg-slate-900 p-6 shadow-2xl shadow-amber-500/10">
        <div className="mb-4 flex items-center gap-3">
          <AlertTriangle className="h-6 w-6 flex-shrink-0 text-amber-400" />
          <h2 className="text-lg font-semibold text-white">
            {t("invites.codeModalTitle")}
          </h2>
        </div>
        <p className="mb-4 text-sm text-slate-300">
          {t("invites.codeModalBody")}
        </p>
        <div className="flex items-center gap-2 rounded-2xl border border-slate-700 bg-slate-950 px-4 py-3">
          <code className="flex-1 break-all font-mono text-sm text-cyan-300">
            {code}
          </code>
          <button
            type="button"
            onClick={handleCopy}
            className="ml-2 flex-shrink-0 rounded-xl p-2 text-slate-400 transition hover:bg-slate-800 hover:text-white"
            title={t("invites.copy")}
          >
            {copied ? (
              <Check className="h-4 w-4 text-emerald-400" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </button>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="mt-6 w-full rounded-2xl bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 py-2.5 text-sm font-semibold text-white shadow-lg shadow-cyan-700/30 transition hover:opacity-90"
        >
          {t("invites.codeModalConfirm")}
        </button>
      </div>
    </div>
  );
};

export default function InviteManagement() {
  const { t } = useTranslation();
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  // Form state
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [lang, setLang] = useState("fr");

  const fetchInvites = useCallback(async () => {
    setLoading(true);
    try {
      const { invites: list } = await adminAPI.listInvites();
      setInvites(list);
    } catch {
      // error toast handled by api interceptor
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInvites();
  }, [fetchInvites]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await adminAPI.createInvite({
        email: email.trim(),
        note: note.trim() || undefined,
        expiresAt: expiresAt || undefined,
        lang,
      });
      toast.success(t("invites.sentSuccess", { email: email.trim() }));
      setEmail("");
      setNote("");
      setExpiresAt("");
      setLang("fr");
      await fetchInvites();
    } catch {
      // handled by interceptor
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id) => {
    try {
      await adminAPI.revokeInvite(id);
      toast.success(t("invites.revokeSuccess"));
      setInvites((prev) =>
        prev.map((inv) => (inv.id === id ? { ...inv, isActive: false } : inv)),
      );
    } catch {
      // handled by interceptor
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-white">
          {t("invites.title")}
        </h1>
        <p className="mt-1 text-sm text-slate-400">{t("invites.subtitle")}</p>
      </div>

      {/* Create form */}
      <div className="rounded-3xl border border-white/10 bg-slate-900/70 p-6 shadow-xl shadow-cyan-500/5 backdrop-blur-xl">
        <h2 className="mb-4 text-base font-semibold text-cyan-100">
          {t("invites.createTitle")}
        </h2>
        <form onSubmit={handleCreate} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-400">
                {t("invites.fieldEmail")}
              </label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("invites.fieldEmailPlaceholder")}
                  className="w-full rounded-2xl border border-slate-700 bg-slate-950/60 py-2.5 pl-9 pr-4 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500/50"
                />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-400">
                {t("invites.fieldNote")}
              </label>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={t("invites.fieldNotePlaceholder")}
                className="w-full rounded-2xl border border-slate-700 bg-slate-950/60 px-4 py-2.5 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500/50"
              />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-400">
                {t("invites.fieldLang")}
              </label>
              <select
                value={lang}
                onChange={(e) => setLang(e.target.value)}
                className="w-full rounded-2xl border border-slate-700 bg-slate-950/60 px-4 py-2.5 text-sm text-slate-100 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500/50"
              >
                <option value="fr">Français</option>
                <option value="de">Deutsch</option>
                <option value="it">Italiano</option>
                <option value="en">English</option>
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-400">
                {t("invites.fieldExpiry")}
              </label>
              <input
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="w-full rounded-2xl border border-slate-700 bg-slate-950/60 px-4 py-2.5 text-sm text-slate-100 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500/50 [color-scheme:dark]"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={creating}
              className="flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 px-8 py-2.5 text-sm font-semibold text-white shadow-lg shadow-cyan-700/30 transition hover:opacity-90 disabled:opacity-50"
            >
              {creating ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <UserPlus className="h-4 w-4" />
              )}
              {t("invites.createButton")}
            </button>
          </div>
        </form>
      </div>

      {/* Invite list */}
      <div className="rounded-3xl border border-white/10 bg-slate-900/70 shadow-xl shadow-cyan-500/5 backdrop-blur-xl overflow-hidden">
        <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
          <h2 className="text-base font-semibold text-cyan-100">
            {t("invites.listTitle")}
          </h2>
          <button
            type="button"
            onClick={fetchInvites}
            disabled={loading}
            className="flex items-center gap-1.5 rounded-xl border border-slate-700 bg-slate-800/60 px-3 py-1.5 text-xs font-medium text-slate-300 transition hover:bg-slate-700 disabled:opacity-50"
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`}
            />
            {t("invites.refresh")}
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 text-sm">
            <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
            {t("common.loading")}
          </div>
        ) : invites.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-400">
            {t("invites.empty")}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/5 text-xs font-medium uppercase tracking-wider text-slate-500">
                  <th className="px-6 py-3 text-left">
                    {t("invites.colEmail")}
                  </th>
                  <th className="px-6 py-3 text-left">
                    {t("invites.colNote")}
                  </th>
                  <th className="px-6 py-3 text-left">
                    {t("invites.colExpiry")}
                  </th>
                  <th className="px-6 py-3 text-left">
                    {t("invites.colStatus")}
                  </th>
                  <th className="px-6 py-3 text-left">
                    {t("invites.colUsedBy")}
                  </th>
                  <th className="px-6 py-3 text-left">
                    {t("invites.colCreated")}
                  </th>
                  <th className="px-6 py-3 text-right">
                    {t("invites.colActions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {invites.map((inv) => (
                  <tr
                    key={inv.id}
                    className="text-slate-300 transition hover:bg-slate-800/30"
                  >
                    <td className="px-6 py-4 font-mono text-xs text-cyan-300">
                      {inv.email || (
                        <span className="text-slate-500 italic">
                          {t("invites.anyEmail")}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-slate-400">
                      {inv.note || "—"}
                    </td>
                    <td className="px-6 py-4 text-xs">
                      {formatDate(inv.expiresAt)}
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge invite={inv} />
                    </td>
                    <td className="px-6 py-4 text-xs">
                      {inv.usedBy ? (
                        <span className="text-slate-300">
                          {inv.usedBy.email}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-6 py-4 text-xs">
                      {formatDate(inv.createdAt)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {inv.isActive && !inv.usedAt && (
                        <button
                          type="button"
                          onClick={() => handleRevoke(inv.id)}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs font-medium text-red-300 transition hover:bg-red-500/20"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          {t("invites.revoke")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
